import React, { useMemo } from "react"

import { Hash, Plus, Trash2 } from "lucide-react"

import { InlineDiff } from "@/components/diff/InlineDiff"
import { HighlightedText } from "@/components/HighlightText"
import {
  DTMF_MAX_DIGITS,
  getScriptTurnKind,
  getScriptTurnText,
  isDtmfScriptTurn,
  setScriptTurnText,
  validateDtmfDigits,
  type ScriptTurn,
} from "@/types/ScriptTurn"

import { useFindReplaceContext } from "../../../contexts/FindReplaceContext"
import type { EditableTestCase } from "../../../model"
import {
  getHighlightState,
  type HighlightState,
} from "../../../utils/findReplaceHighlight"
import { InputCellWrapper } from "./InputCellWrapper"

interface ScriptInputCellProps {
  testCase: EditableTestCase
  pendingEdit: Partial<EditableTestCase> | null
  isExpanded: boolean
  onUpdate: (updates: Partial<EditableTestCase>) => void
  width: string
}

function turnToDiffLine(turn: ScriptTurn): string {
  const kind = getScriptTurnKind(turn)
  if (kind === "text") {
    return `> ${getScriptTurnText(turn) ?? ""}`
  }
  if (kind === "dtmf" && isDtmfScriptTurn(turn)) {
    return `# ${turn.digits}`
  }
  const type =
    typeof turn === "object" && turn !== null ? String(turn.type) : "unknown"
  return `? [${type}]`
}

export const ScriptInputCell: React.FC<ScriptInputCellProps> = ({
  testCase,
  pendingEdit,
  isExpanded,
  onUpdate,
  width,
}) => {
  const { findReplaceState } = useFindReplaceContext()
  const turns: ScriptTurn[] = testCase.script_turns || []
  const pendingTurns = pendingEdit?.script_turns
  const pendingInputStr = pendingEdit?.input_str
  const hasPendingTurns = pendingTurns !== undefined && pendingTurns !== null
  const hasPendingInputStr =
    !hasPendingTurns &&
    pendingInputStr !== undefined &&
    pendingInputStr !== null
  const hasPendingEdit = hasPendingTurns || hasPendingInputStr

  const newTurns: ScriptTurn[] = useMemo(() => {
    if (hasPendingTurns) {
      return pendingTurns
    }
    if (hasPendingInputStr) {
      return pendingInputStr.split("\n")
    }
    return []
  }, [hasPendingTurns, pendingTurns, hasPendingInputStr, pendingInputStr])

  const updateTurnText = (index: number, value: string) => {
    const updated = [...turns]
    updated[index] = setScriptTurnText(updated[index], value)
    onUpdate({ script_turns: updated })
  }

  const updateDtmfDigits = (index: number, value: string) => {
    const updated = [...turns]
    const current = updated[index]
    if (isDtmfScriptTurn(current)) {
      updated[index] = { ...current, digits: value }
      onUpdate({ script_turns: updated })
    }
  }

  const addTextTurn = () => {
    onUpdate({ script_turns: [...turns, ""] })
  }

  const addDtmfTurn = () => {
    onUpdate({
      script_turns: [...turns, { type: "dtmf", digits: "" } as ScriptTurn],
    })
  }

  const removeTurn = (index: number) => {
    onUpdate({ script_turns: turns.filter((_, i) => i !== index) })
  }

  return (
    <InputCellWrapper
      width={width}
      isExpanded={isExpanded}
      hasPendingEdit={hasPendingEdit}
      ariaLabel="Edit script test case"
    >
      {({ isEditing }) => (
        <div className="flex-1 min-w-0">
          {hasPendingEdit ? (
            <ScriptTurnsDiff oldTurns={turns} newTurns={newTurns} />
          ) : isEditing ? (
            <div className="space-y-2">
              {turns.map((turn, index) => (
                <ScriptTurnEditorRow
                  key={index}
                  index={index}
                  turn={turn}
                  onUpdateText={(value) => updateTurnText(index, value)}
                  onUpdateDigits={(value) => updateDtmfDigits(index, value)}
                  onRemove={() => removeTurn(index)}
                />
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    addTextTurn()
                  }}
                  className="flex-1 px-2 py-2 text-sm bg-surface-1 rounded outline-none text-coval-text-muted hover:bg-surface-2 hover:text-coval-text-muted transition-colors text-left"
                >
                  <span className="inline-flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    text turn
                  </span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    addDtmfTurn()
                  }}
                  className="flex-1 px-2 py-2 text-sm bg-surface-1 rounded outline-none text-coval-text-muted hover:bg-surface-2 hover:text-coval-text-muted transition-colors text-left"
                >
                  <span className="inline-flex items-center gap-1">
                    <Hash className="w-4 h-4" />
                    DTMF turn
                  </span>
                </button>
              </div>
            </div>
          ) : turns.length > 0 ? (
            <div className="text-sm">
              {turns.map((turn, index) => (
                <ScriptTurnReadRow
                  key={index}
                  turn={turn}
                  highlight={getHighlightState(
                    findReplaceState,
                    testCase.id,
                    "script_turns",
                    index.toString(),
                  )}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-coval-text-muted">No script turns</p>
          )}
        </div>
      )}
    </InputCellWrapper>
  )
}

interface ScriptTurnEditorRowProps {
  index: number
  turn: ScriptTurn
  onUpdateText: (value: string) => void
  onUpdateDigits: (value: string) => void
  onRemove: () => void
}

const ScriptTurnEditorRow: React.FC<ScriptTurnEditorRowProps> = ({
  index,
  turn,
  onUpdateText,
  onUpdateDigits,
  onRemove,
}) => {
  const kind = getScriptTurnKind(turn)

  if (kind === "dtmf" && isDtmfScriptTurn(turn)) {
    const validation = validateDtmfDigits(turn.digits)
    const helperMessage = validation.error ?? validation.warning
    const helperTone = validation.error
      ? "text-coval-danger-text"
      : validation.warning
        ? "text-coval-warning-text"
        : null

    return (
      <div className="flex gap-2 items-start group">
        <label
          className={`flex-1 min-w-0 rounded px-2 py-1 transition-colors min-h-8 cursor-text flex items-center gap-2 ${
            validation.error ? "bg-coval-danger-surface" : "bg-surface-1"
          }`}
        >
          <Hash
            className="w-4 h-4 text-coval-text-muted flex-shrink-0"
            aria-hidden="true"
          />
          <span className="sr-only">DTMF digits for turn {index + 1}</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. 1, 9658, *#"
            value={turn.digits}
            onChange={(e) => {
              const next = e.target.value.replace(/[^0-9*#]/g, "")
              onUpdateDigits(next)
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault()
                ;(e.target as HTMLInputElement).blur()
              }
            }}
            aria-label={`DTMF digits for turn ${index + 1}`}
            aria-invalid={validation.error ? true : undefined}
            className="w-full text-sm text-coval-text bg-transparent outline-none font-mono"
            maxLength={DTMF_MAX_DIGITS * 2}
          />
        </label>
        <button
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-coval-danger-text hover:text-coval-danger-text p-1 mt-1"
          title="Delete turn"
          aria-label="Delete turn"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {helperMessage && helperTone && (
          <p
            className={`text-xs ${helperTone} basis-full pl-6 -mt-1`}
            role="alert"
          >
            {helperMessage}
          </p>
        )}
      </div>
    )
  }

  if (kind === "unknown") {
    const turnType =
      typeof turn === "object" && turn !== null ? String(turn.type) : "unknown"
    return (
      <div className="flex gap-2 items-start group">
        <div className="flex-1 min-w-0 bg-surface-0 border border-dashed border-coval-border-active rounded px-2 py-1 min-h-8 flex items-center text-sm text-coval-text-muted italic">
          Unsupported turn type
          <span className="ml-1 font-mono not-italic">{turnType}</span>
          <span className="ml-1">— preserved on save</span>
        </div>
        <button
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-coval-danger-text hover:text-coval-danger-text p-1 mt-1"
          title="Delete turn"
          aria-label="Delete turn"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    )
  }

  const textValue = getScriptTurnText(turn) ?? ""
  return (
    <div className="flex gap-2 items-start group">
      <label className="flex-1 min-w-0 bg-surface-1 rounded px-2 py-1 transition-colors min-h-8 cursor-text">
        <textarea
          ref={(el) => {
            if (el) {
              el.style.height = "auto"
              el.style.height = `${el.scrollHeight}px`
            }
          }}
          onFocus={(e) => {
            const len = e.target.value.length
            e.target.setSelectionRange(len, len)
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              ;(e.target as HTMLTextAreaElement).blur()
            }
          }}
          rows={1}
          placeholder="Enter turn..."
          value={textValue}
          onChange={(e) => {
            onUpdateText(e.target.value)
            e.target.style.height = "auto"
            e.target.style.height = `${e.target.scrollHeight}px`
          }}
          aria-label={`Turn ${index + 1}`}
          className="w-full text-sm text-coval-text bg-transparent outline-none resize-none overflow-hidden"
        />
      </label>
      <button
        type="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-coval-danger-text hover:text-coval-danger-text p-1 mt-1"
        title="Delete turn"
        aria-label="Delete turn"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  )
}

const ScriptTurnReadRow: React.FC<{
  turn: ScriptTurn
  highlight: HighlightState
}> = ({ turn, highlight }) => {
  const kind = getScriptTurnKind(turn)

  if (kind === "dtmf" && isDtmfScriptTurn(turn)) {
    return (
      <p className="text-foreground whitespace-pre-wrap break-words mt-2 first:mt-0 inline-flex items-center gap-1">
        <Hash
          className="w-3.5 h-3.5 text-coval-text-muted"
          aria-hidden="true"
        />
        <span className="font-mono">{turn.digits || "(empty)"}</span>
      </p>
    )
  }

  if (kind === "unknown") {
    const turnType =
      typeof turn === "object" && turn !== null ? String(turn.type) : "unknown"
    return (
      <p className="text-coval-text-muted italic whitespace-pre-wrap break-words mt-2 first:mt-0">
        Unsupported turn type{" "}
        <span className="font-mono not-italic">{turnType}</span>
      </p>
    )
  }

  return (
    <p className="text-foreground whitespace-pre-wrap break-words mt-2 first:mt-0">
      <span className="text-coval-text-muted">&gt;</span>{" "}
      {highlight.highlightText ? (
        <HighlightedText
          text={getScriptTurnText(turn) ?? ""}
          searchText={highlight.highlightText}
          isCurrentMatch={highlight.isCurrentMatch}
        />
      ) : (
        getScriptTurnText(turn)
      )}
    </p>
  )
}

/** Diff view using line-aware diffing so insertions/removals don't cascade. */
const ScriptTurnsDiff: React.FC<{
  oldTurns: ScriptTurn[]
  newTurns: ScriptTurn[]
}> = ({ oldTurns, newTurns }) => {
  const oldText = useMemo(
    () => oldTurns.map(turnToDiffLine).join("\n"),
    [oldTurns],
  )
  const newText = useMemo(
    () => newTurns.map(turnToDiffLine).join("\n"),
    [newTurns],
  )

  return (
    <div className="text-sm whitespace-pre-wrap break-words">
      <InlineDiff oldText={oldText} newText={newText} mode="char" />
    </div>
  )
}
